(() => {
  if (window.SkinNoteGA4Events) return;
  window.SkinNoteGA4Events = true;

  const MAX_TEXT = 80;
  const PRODUCT_RE = /\/products\/([^/?#]+)/;
  const COLLECTION_RE = /\/collections\/([^/?#]+)/;
  const SENSITIVE_RE = /@|\b\d{7,}\b/;
  const lastPublished = new Map();

  const cleanText = (value, max = MAX_TEXT) =>
    String(value || "").replace(/\s+/g, " ").trim().slice(0, max);

  const pathFromUrl = (url) => {
    if (!url) return "";
    try {
      return new URL(url, window.location.origin).pathname;
    } catch (error) {
      return "";
    }
  };

  const handleFromHref = (href, pattern) => {
    const path = pathFromUrl(href);
    const match = path.match(pattern);
    return match ? decodeURIComponent(match[1]) : "";
  };

  const productHandleFromHref = (href) => handleFromHref(href, PRODUCT_RE);
  const collectionHandleFromHref = (href) => handleFromHref(href, COLLECTION_RE);

  const closest = (target, selector) =>
    target && typeof target.closest === "function" ? target.closest(selector) : null;

  const sanitizeSearchTerm = (value) => {
    const term = cleanText(value, 60).toLowerCase();
    if (!term) return "";
    return SENSITIVE_RE.test(term) ? "[redacted]" : term;
  };

  const getLinkTargetPath = (element) =>
    pathFromUrl(element?.href || element?.getAttribute?.("formaction") || "");

  const getProductContext = (element) => {
    const card = closest(element, ".product-card__container, .card-wrapper, .product-card, li");
    const link =
      closest(element, 'a[href*="/products/"]') ||
      card?.querySelector?.('a[href*="/products/"]') ||
      closest(element, '[data-product-url]');
    const productUrl = link?.href || link?.getAttribute?.("data-product-url") || "";
    const list = closest(element, "[data-cart-essentials], .collection-grid__container, .template-search__container, .search-modal__container, .featured-collection, .product-recommendations");
    const siblings = card?.parentElement ? Array.from(card.parentElement.children) : [];
    const position = card && siblings.length ? siblings.indexOf(card) + 1 : undefined;

    return {
      product_handle: productHandleFromHref(productUrl),
      product_title: cleanText(
        card?.querySelector?.(".product-card__title, .card__heading, .cart-essentials__title, .predictive-search-results__title")?.textContent ||
          link?.textContent
      ),
      list_name: cleanText(list?.getAttribute?.("data-section-name") || list?.className || "site"),
      position,
    };
  };

  const publish = (eventName, payload = {}, options = {}) => {
    if (!window.Shopify?.analytics?.publish) return;

    const now = Date.now();
    const dedupeKey = `${eventName}:${JSON.stringify(payload)}`;
    const lastTime = lastPublished.get(dedupeKey) || 0;
    if (!options.allowRepeat && now - lastTime < 600) return;
    lastPublished.set(dedupeKey, now);

    window.Shopify.analytics.publish(eventName, {
      page_path: window.location.pathname,
      page_title: document.title,
      viewport: window.matchMedia("(max-width: 749px)").matches ? "mobile" : "desktop",
      ...payload,
    });
  };

  const publishPageContext = () => {
    const path = window.location.pathname;
    const collectionHandle = productHandleFromHref(path) ? "" : collectionHandleFromHref(path);

    if (collectionHandle) {
      publish("skn_collection_view", {
        collection_handle: collectionHandle,
        collection_title: cleanText(document.querySelector("h1")?.textContent),
      });
      return;
    }

    if (path.includes("/search")) {
      const params = new URLSearchParams(window.location.search);
      const term = sanitizeSearchTerm(params.get("q"));
      if (term) {
        publish("skn_search_results_view", {
          search_term: term,
          search_term_length: term === "[redacted]" ? undefined : term.length,
        });
      }
    }
  };

  document.addEventListener(
    "click",
    (event) => {
      const target = event.target;

      const menuTrigger = closest(
        target,
        '#MenuDrawer-HamburgerIcon, summary.header__icon--menu, button[aria-controls*="menu"], .header__icon--menu, [data-drawer-open="menu"]'
      );
      if (menuTrigger) {
        publish("skn_menu_open", { source: "header" });
        return;
      }

      const searchTrigger = closest(
        target,
        '.header__icon--search, summary.header__icon--search, button[aria-controls*="Search"], [data-search-open]'
      );
      if (searchTrigger) {
        publish("skn_search_open", { source: "header" });
        return;
      }

      const cartTrigger = closest(
        target,
        'a[href="/cart"], a[href*="/cart"], button[name="cart"], [data-cart-drawer-trigger], cart-drawer-opener, .header__icon--cart'
      );
      if (cartTrigger) {
        publish("skn_cart_open", { source: closest(cartTrigger, "header") ? "header" : "site" });
        return;
      }

      const checkoutButton = closest(
        target,
        'button[name="checkout"], a[href*="/checkout"], .cart__checkout-button, #CartDrawer-Checkout'
      );
      if (checkoutButton) {
        publish("skn_checkout_click", {
          source: closest(checkoutButton, "cart-drawer, #CartDrawer") ? "cart_drawer" : "cart_page",
        });
        return;
      }

      const filterOpen = closest(target, ".js-open-facets, [aria-controls*='Facet'], [aria-controls*='filter']");
      if (filterOpen) {
        publish("skn_filter_open", {
          collection_handle: collectionHandleFromHref(window.location.pathname),
        });
      }

      const quickAdd = closest(target, "quick-add-opener button, .quick-add__submit");
      if (quickAdd && !quickAdd.disabled && quickAdd.getAttribute("aria-disabled") !== "true") {
        const context = getProductContext(quickAdd);
        publish("skn_quick_add_open", {
          ...context,
          source: closest(quickAdd, ".search-modal__container")
            ? "search_drawer"
            : closest(quickAdd, "[data-cart-essentials]")
              ? "cart_essentials"
              : "product_grid",
        });
      }

      const essentialAdd = closest(target, "[data-cart-essentials-add]");
      if (essentialAdd && !essentialAdd.disabled && essentialAdd.getAttribute("aria-disabled") !== "true") {
        publish("skn_essential_add_click", {
          ...getProductContext(essentialAdd),
          source: "cart_essentials",
        });
        return;
      }

      const addToCart = closest(target, 'button[name="add"], button[type="submit"].quick-add__submit, product-form button[type="submit"]');
      if (addToCart && !addToCart.disabled && addToCart.getAttribute("aria-disabled") !== "true") {
        publish("skn_add_to_cart_click", {
          ...getProductContext(addToCart),
          source: closest(addToCart, "quick-add-modal") ? "quick_add" : closest(addToCart, ".product") ? "product_page" : "product_grid",
        });
      }

      const predictiveResult = closest(target, ".predictive-search-results__card");
      if (predictiveResult) {
        publish("skn_predictive_result_click", {
          ...getProductContext(predictiveResult),
          target_path: getLinkTargetPath(predictiveResult),
          result_type: predictiveResult.href?.includes("/collections/") ? "collection" : "product",
        });
        return;
      }

      const productLink = closest(target, 'a[href*="/products/"]');
      if (productLink && !closest(target, ".cart-essentials__add, .quick-add__submit")) {
        publish("skn_product_card_click", {
          ...getProductContext(productLink),
          target_path: getLinkTargetPath(productLink),
        });
        return;
      }

      const collectionLink = closest(target, 'a[href*="/collections/"]');
      if (collectionLink) {
        publish("skn_collection_click", {
          collection_handle: collectionHandleFromHref(collectionLink.href),
          link_text: cleanText(collectionLink.textContent),
          target_path: getLinkTargetPath(collectionLink),
        });
      }

      const cta = closest(target, "a.button, button.button, .btn, .cta-btn, [class*='button']");
      if (cta) {
        publish("skn_cta_click", {
          cta_text: cleanText(cta.textContent || cta.getAttribute("aria-label")),
          target_path: getLinkTargetPath(cta),
          source: closest(cta, "header") ? "header" : closest(cta, "footer") ? "footer" : "body",
        });
      }
    },
    { capture: true }
  );

  document.addEventListener(
    "submit",
    (event) => {
      const form = event.target;
      if (!form?.matches?.('form[action*="/search"], predictive-search form, .search-modal form')) return;

      const input = form.querySelector('input[type="search"], input[name="q"]');
      const term = sanitizeSearchTerm(input?.value);

      publish("skn_search_submit", {
        search_term: term,
        search_term_length: term === "[redacted]" ? undefined : term.length,
        source: closest(form, ".search-modal__container") ? "search_drawer" : "search_page",
      }, { allowRepeat: true });
    },
    { capture: true }
  );

  document.addEventListener(
    "change",
    (event) => {
      const element = event.target;
      if (!element?.matches?.('input[name^="filter."], select[name="sort_by"], .facets input, .facets select, .mobile-facets__drawer input, .mobile-facets__drawer select')) {
        return;
      }

      publish("skn_collection_filter_use", {
        collection_handle: collectionHandleFromHref(window.location.pathname),
        filter_name: cleanText(element.name || element.getAttribute("aria-label")),
        filter_value: cleanText(element.value),
        control_type: element.tagName.toLowerCase(),
      }, { allowRepeat: true });
    },
    { capture: true }
  );

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", publishPageContext, { once: true });
  } else {
    publishPageContext();
  }
})();
